import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { Star, X, GripVertical } from 'lucide-react';
import { cn } from '../lib/utils';
import { commKey, isPreferred } from '../lib/contactFormHelpers';

interface CommMethod {
  type: string;
  value: string;
}

function SortableItem({
  method,
  index,
  preferredCommMethod,
  onSetPreferred,
  onRemove,
}: {
  method: CommMethod;
  index: number;
  preferredCommMethod: string;
  onSetPreferred: (key: string) => void;
  onRemove: (index: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: commKey(method),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 p-3 bg-slate-50 border rounded-xl transition-shadow',
        isPreferred(preferredCommMethod, method)
          ? 'border-[#fa8585] bg-[#fa8585]/5'
          : 'border-slate-200',
        isDragging && 'shadow-lg z-10 opacity-90',
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 -ml-1 touch-none cursor-grab active:cursor-grabbing"
      >
        <GripVertical size={18} className="text-slate-300" />
      </button>
      <button onClick={() => onSetPreferred(commKey(method))} className="p-1 -ml-1">
        <Star
          size={18}
          className={
            isPreferred(preferredCommMethod, method)
              ? 'text-[#fa8585] fill-[#fa8585]'
              : 'text-slate-300'
          }
        />
      </button>
      <span
        className={cn(
          'text-xs font-bold uppercase shrink-0 min-w-16',
          isPreferred(preferredCommMethod, method) ? 'text-[#fa8585]' : 'text-slate-500',
        )}
      >
        {method.type}
      </span>
      <span className="text-sm font-medium text-slate-900 flex-1 break-all">{method.value}</span>
      <button
        onClick={() => onRemove(index)}
        className="p-1 text-slate-400 hover:text-red-500 transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
}

export default function SortableCommList({
  communicationMethods,
  preferredCommMethod,
  onSetPreferred,
  onRemove,
  onReorder,
}: {
  communicationMethods: CommMethod[];
  preferredCommMethod: string;
  onSetPreferred: (key: string) => void;
  onRemove: (index: number) => void;
  onReorder: (newOrder: CommMethod[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = communicationMethods.findIndex((m) => commKey(m) === active.id);
    const newIndex = communicationMethods.findIndex((m) => commKey(m) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = [...communicationMethods];
    const [moved] = newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, moved);
    onReorder(newOrder);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext
        items={communicationMethods.map((m) => commKey(m))}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2 mb-3">
          {communicationMethods.map((method, index) => (
            <SortableItem
              key={commKey(method)}
              method={method}
              index={index}
              preferredCommMethod={preferredCommMethod}
              onSetPreferred={onSetPreferred}
              onRemove={onRemove}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
